
import React from 'react';
import { TranscriptionJob, TranscriptionStatus } from '../types';
import { FileAudio, RefreshCw, CheckCircle2, XCircle, Clock, CloudCog, StopCircle } from 'lucide-react';

interface JobItemProps {
  job: TranscriptionJob;
  onView: () => void;
  onRetry?: (job: TranscriptionJob) => void;
  onCancel?: (jobId: string) => void;
}

const JobItem: React.FC<JobItemProps> = ({ job, onView, onRetry, onCancel }) => {
  const getStatusConfig = () => {
    switch (job.status) {
      case TranscriptionStatus.UPLOADING:
        return {
             icon: <Clock className="w-4 h-4" />, 
             label: 'Queued', 
             style: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20' 
        };
      case TranscriptionStatus.PROCESSING:
        return { 
            icon: <RefreshCw className="w-4 h-4 animate-spin" />, 
            label: 'Processing', 
            style: 'bg-brand-primary/10 text-brand-primary border-brand-primary/20' 
        };
      case TranscriptionStatus.SUCCESS:
        return { 
            icon: <CheckCircle2 className="w-4 h-4" />, 
            label: 'Complete', 
            style: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20' 
        };
      case TranscriptionStatus.ERROR:
        return { 
            icon: <XCircle className="w-4 h-4" />, 
            label: 'Failed', 
            style: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' 
        };
      default:
        return { icon: null, label: '', style: '' };
    }
  };

  const status = getStatusConfig();
  const canRetry = onRetry && job.file && job.file.size > 0;

  return (
    <div id={`job-${job.id}`} className="group bg-white dark:bg-dark-200 p-4 rounded-xl border border-base-200 dark:border-dark-300 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between gap-4 animate-slide-in-right">
      <div className="flex items-center gap-4 overflow-hidden min-w-0">
        <div className="w-12 h-12 flex-shrink-0 bg-base-200 dark:bg-dark-300 rounded-lg flex items-center justify-center text-base-content/70 dark:text-dark-content/70 group-hover:bg-brand-primary/10 group-hover:text-brand-primary transition-colors">
            <FileAudio className="w-6 h-6" />
        </div>
        <div className="overflow-hidden min-w-0 flex flex-col gap-1">
          <p className="font-semibold text-base-content dark:text-dark-content truncate" title={job.fileName}>{job.fileName}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border w-fit ${status.style}`}>
              {status.icon}
              {status.label}
            </div>
            {job.isBackground && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20 w-fit">
                <CloudCog className="w-3 h-3" />
                Background
              </div>
            )}
          </div>
          {job.status === TranscriptionStatus.PROCESSING && (
            <div className="flex items-center gap-2 mt-0.5">
              <div className="flex-1 h-1 bg-base-200 dark:bg-dark-300 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full transition-all duration-500"
                  style={{ width: `${job.progress ?? 0}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-base-content/50 dark:text-dark-content/50 flex-shrink-0 w-7 text-right">
                {job.progress ?? 0}%
              </span>
            </div>
          )}
          {job.status === TranscriptionStatus.PROCESSING && job.partialTranscript && (
            <p className="text-xs text-base-content/50 dark:text-dark-content/50 truncate max-w-xs font-mono">
              {job.partialTranscript.split('\n').at(-1)}
            </p>
          )}
        </div>
      </div>
      
      <div className="flex-shrink-0 flex items-center gap-3">
        {job.status === TranscriptionStatus.PROCESSING && onCancel && (
          <button
            onClick={() => onCancel(job.id)}
            title="Cancel job"
            className="flex items-center gap-1.5 text-sm font-semibold py-2 px-3 rounded-lg border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all"
          >
            <StopCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Cancel</span>
          </button>
        )}
        {job.status === TranscriptionStatus.SUCCESS && (
          <button onClick={onView} className="flex items-center gap-2 bg-brand-primary text-white text-sm font-semibold py-2 px-4 rounded-lg shadow-lg shadow-brand-primary/20 hover:bg-brand-dark hover:scale-105 active:scale-95 transition-all">
            <span className="hidden sm:inline">View Transcript</span>
            <span className="sm:hidden">View</span>
          </button>
        )}
        {job.status === TranscriptionStatus.ERROR && (
          <div className="flex items-center gap-3">
            <p className="hidden md:block text-xs text-red-500 max-w-[150px] truncate" title={job.error}>{job.error}</p>
            {canRetry && (
               <button onClick={() => onRetry(job)} className="bg-base-200 dark:bg-dark-300 text-base-content dark:text-dark-content text-sm font-semibold py-2 px-4 rounded-lg hover:bg-base-300 dark:hover:bg-dark-100 transition-colors">
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobItem;
